/**
 * Vitality's own data: the locations picker (WP-00's `GET
 * /companies/{companyId}/locations`, not yet in the generated
 * `src/api/schema.ts` — same situation `src/features/statistics/api.ts`
 * documents for `/statistics/*`, so this uses the same hand-built
 * `request()`/`buildUrl` approach rather than waiting on `generate:api`)
 * and the dashboard-batch panels behind the 7 cards.
 */

import { buildUrl, readReason } from '@/api/client';
import { errorForStatus, NetworkError } from '@/api/errors';
import { getAccessToken } from '@/features/auth/token';
import type {
  DashboardPanelSpec,
  DashboardRange,
  DashboardTarget,
} from '@/features/statistics/api';
import { dailySeriesPanel } from '@/features/vitality/series';

export interface Location {
  id: string;
  name: string;
  city: string | null;
  country: string | null;
}

/** Mirrors `src/features/statistics/api.ts`'s private `request()`. */
async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  options: { params?: Record<string, string>; body?: unknown } = {},
): Promise<T> {
  const url = buildUrl(path, options.params);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: options.body !== undefined ? JSON.stringify(options.body) : null,
    });
  } catch (cause) {
    throw new NetworkError(`Request to ${path} failed: ${String(cause)}`);
  }

  if (!response.ok) {
    throw errorForStatus(response.status, `${method} ${path} failed`, await readReason(response));
  }

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  if (text === '') return undefined as T;
  return JSON.parse(text) as T;
}

export function listLocations(companyId: string): Promise<Location[]> {
  return request('GET', '/companies/{companyId}/locations', { params: { companyId } });
}

/** A dashboard-batch target, plus the optional `location_id` WP-02 added to
 * the API's department-target resolution (`php/api_statistics.php`,
 * `resolveDepartmentTarget`) — only meaningful alongside `department_id`/
 * `company_id` — and `role`, narrowing the pool to people holding (or not
 * holding) a `company_admin`/`department_admin`/`admin` grant, valid
 * alongside `company_id`/`department_id` (not `user_id`, a single-person
 * target `role` has nothing to narrow). */
export type VitalityTarget = DashboardTarget & {
  location_id?: string;
  role?: 'manager' | 'employee';
};

const METRIC_KEYS = ['sleep_duration', 'steps', 'hrv', 'stress'] as const;
export type VitalityMetricKey = (typeof METRIC_KEYS)[number];

/**
 * Today minus `days` full days, ending yesterday — the window immediately
 * before `range`, same length, for the resting-heart-rate trend (see
 * `restingHeartRatePanels` below for why that one isn't part of the
 * `compare_to: previous_period` batch panel).
 */
export function previousPeriod(from: string, to: string): { from: string; to: string } {
  const fromDate = new Date(`${from}T00:00:00Z`);
  const toDate = new Date(`${to}T00:00:00Z`);
  const days = Math.round((toDate.getTime() - fromDate.getTime()) / 86_400_000) + 1;
  const prevTo = new Date(fromDate.getTime() - 86_400_000);
  const prevFrom = new Date(prevTo.getTime() - (days - 1) * 86_400_000);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(prevFrom), to: iso(prevTo) };
}

/** `range` resolved to explicit `{from, to}` — the panel builder needs real
 * dates for the resting-heart-rate previous-period panel; a relative
 * keyword (`last_30_days`) is expanded the same way the API's own
 * `resolveRange()` does for the presets `RangePicker` offers. */
export function resolveExplicitRange(range: DashboardRange): { from: string; to: string } {
  if (typeof range === 'object') return range;
  const to = new Date().toISOString().slice(0, 10);
  const days = range === 'last_7_days' ? 6 : range === 'this_week' ? 6 : 29;
  const from = new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
  return { from, to };
}

/**
 * The 4 panels one Vitality-page fetch needs: `trend` (sleep/steps/hrv/
 * stress, current vs. previous period via the batch endpoint's own
 * `compare_to`), `resting-current`/`resting-previous` (heart_rate, one
 * panel per period — see `restingHeartRateFromSeries`), and `daily` (all 5
 * metrics at `grain: 'day'`, see `series.ts#deriveDailySeries`) for the
 * cards' sparklines.
 *
 * `trend` bundles all 4 measured metrics into one panel rather than one
 * per metric. That used to silently mask a thin metric behind a popular
 * one — a department where everyone logs steps but almost nobody logs HRV
 * would clear the group-size floor on steps alone and the whole panel came
 * back `status: 'ok'`, rendering HRV as a generic empty series instead of
 * "not enough people share this metric". Fixed at the source instead of
 * worked around here: `php/api_statistics.php`'s `resolveDepartmentTarget`/
 * `resolveCompanyTarget` now also compute a per-metric contributor count
 * (`stats_reader::contributorCountsByMetric`) for any `series` panel
 * requesting more than one metric, and the response carries a
 * `metric_status` map alongside `delta`/`series` — see
 * `DashboardPanelResult.metric_status` (`statistics/api.ts`) and
 * `deriveCards.ts`, which reads each metric's own entry there instead of
 * the panel's single blanket `status`.
 */
export function vitalityPanels(
  target: VitalityTarget,
  range: DashboardRange,
): DashboardPanelSpec[] {
  const explicit = resolveExplicitRange(range);
  const previous = previousPeriod(explicit.from, explicit.to);
  return [
    {
      id: 'trend',
      type: 'series',
      target,
      grain: 'range',
      range: explicit,
      compare_to: 'previous_period',
      metrics: [...METRIC_KEYS],
    },
    {
      id: 'resting-current',
      type: 'series',
      target,
      grain: 'range',
      range: explicit,
      metrics: ['heart_rate'],
    },
    {
      id: 'resting-previous',
      type: 'series',
      target,
      grain: 'range',
      range: previous,
      metrics: ['heart_rate'],
    },
    dailySeriesPanel(target, explicit),
  ];
}
